import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import WeeklyCardBack from './WeeklyCardBack';
import { trackCardFlipAndAwardXP, type WeeklyCardType } from '../lib/weeklyCardFlipService';
import { useAuth } from '../lib/AuthContext';

interface CivicCardProps {
  categoryLabel: string;
  title: string;
  subtitle: string;
  body?: string;
  meta?: string;
  locationLabel?: string;
  onClick: () => void;
  variant: 'wisdom' | 'moment' | 'spark';
  imageUrl?: string | null;
  // New modular back props
  cardType?: 'artist_wisdom' | 'civic_moment' | 'civic_spark';
  artistImage?: string;
  countryImage?: string;
  countryName?: string;
  countryCode?: string;
  eventDate?: string;
  sourceName?: string;
  sourceUrl?: string;
  quote?: string;
  attribution?: string;
  headline?: string;
  explanation?: string;
  question?: string;
  metadata?: {
    origin?: string;
    genre?: string;
    type?: string;
    impact?: string;
    focus?: string;
    difficulty?: string;
  };
  weekId?: string;
}

export default function CivicCard({
  categoryLabel,
  title,
  subtitle,
  body,
  meta,
  locationLabel,
  onClick,
  variant,
  imageUrl,
  cardType,
  artistImage,
  countryImage,
  countryName,
  countryCode,
  eventDate,
  sourceName,
  sourceUrl,
  quote,
  attribution,
  headline,
  explanation,
  question,
  metadata,
  weekId,
}: CivicCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const { user } = useAuth();

  // Fallback logic for back-side content
  const displayQuote = quote || body || title;
  const displayAttribution = attribution || metadata?.origin || '';
  const displayHeadline = headline || title;
  const displayExplanation = explanation || body || '';
  const displayQuestion = question || title;

  const getMediaIcon = () => {
    switch (variant) {
      case 'wisdom':
        return '🎤';
      case 'moment':
        return '✊🏼';
      case 'spark':
        return '✨';
      default:
        return '🎵';
    }
  };


  const getShortTitle = () => {
    switch (variant) {
      case 'wisdom':
        return 'Artist Wisdom';
      case 'moment':
        return 'Civic Moment';
      case 'spark':
        return 'Spark a Thought';
      default:
        return categoryLabel;
    }
  };

  const getCardSubtitle = () => {
    switch (variant) {
      case 'wisdom':
        return 'An artist reflecting the world.';
      case 'moment':
        return 'A moment shaping the world.';
      case 'spark':
        return 'A question to carry into your week.';
      default:
        return '';
    }
  };

  const handleFlip = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isFlipped && user && weekId && cardType) {
      const normalizedCardType = cardType === 'civic_spark' ? 'spark_a_thought' : cardType;

      const result = await trackCardFlipAndAwardXP(
        user.id,
        weekId,
        normalizedCardType as WeeklyCardType
      );

      if (result.success && !result.alreadyFlipped && result.xpAwarded) {
        console.log(`[CivicCard] Awarded 1 XP for flipping ${normalizedCardType}`);
      }
    }

    setIsFlipped(!isFlipped);
  };

  const handleBackClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFlipped(false);
  };

  return (
    <div
      className="w-full h-[480px] perspective-1000"
      style={{ perspective: '1000px' }}
    >
      <div
        className="relative w-full h-full transition-transform duration-700 preserve-3d"
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* FRONT OF CARD */}
        <button
          onClick={handleFlip}
          className="absolute inset-0 w-full h-full bg-bg-card rounded-[20px] overflow-hidden
            border border-stroke
            flex flex-col backface-hidden"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            zIndex: isFlipped ? 1 : 2,
            isolation: 'isolate',
            boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)',
          }}
          aria-label={`${categoryLabel}: Tap to flip`}
        >
          {/* Hero Icon Panel */}
          <div className="relative w-full h-[50%] rounded-t-[20px]
            bg-[#1e1e1e]
            flex items-center justify-center"
          >
            {/* Consistent overlay to normalize emoji brightness */}
            <div className="absolute inset-0 bg-black/[0.12] rounded-t-[20px]" />

            {/* Emoji icon */}
            <div className="relative text-[5.4rem] z-10">
              {getMediaIcon()}
            </div>
          </div>

          {/* Typography Section - Clean Hierarchy */}
          <div className="flex-1 flex flex-col items-center justify-start px-6 pt-10 pb-12">
            {/* Title */}
            <h1 className="text-[28px] font-semibold text-text-primary text-center leading-tight">
              {getShortTitle()}
            </h1>

            {/* Subtitle */}
            <p className="text-[14px] text-text-secondary text-center leading-snug mt-8">
              {getCardSubtitle()}
            </p>
          </div>

          {/* Tap to Flip Indicator - Bottom Right */}
          <div className="absolute bottom-6 right-5 flex items-center gap-1.5">
            <span className="text-[10px] text-[#40E0D0] font-medium">
              Tap to flip
            </span>
            <ArrowRight className="w-3.5 h-3.5 text-[#40E0D0]" />
          </div>
        </button>

        {/* BACK OF CARD - NEW MODULAR DESIGN */}
        <button
          onClick={handleBackClick}
          className="absolute inset-0 w-full h-full bg-bg-card rounded-[20px] overflow-hidden
            border border-stroke
            backface-hidden"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            zIndex: isFlipped ? 2 : 1,
            isolation: 'isolate',
            boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)',
          }}
          aria-label="Tap to flip back to front"
        >
          <WeeklyCardBack
            cardType={cardType || 'artist_wisdom'}
            imageUrl={imageUrl}
            content={cardType === 'artist_wisdom' ? displayQuote : (cardType === 'civic_moment' ? (subtitle || displayExplanation) : title)}
            attribution={cardType === 'artist_wisdom' ? (subtitle || displayAttribution) : undefined}
            title={cardType === 'civic_moment' ? title : undefined}
            subtext={cardType === 'civic_spark' ? subtitle : undefined}
            countryName={cardType === 'civic_moment' ? countryName : undefined}
            countryCode={cardType === 'civic_moment' ? countryCode : undefined}
            eventDate={cardType === 'civic_moment' ? eventDate : undefined}
            sourceName={cardType === 'civic_moment' ? sourceName : undefined}
            sourceUrl={cardType === 'civic_moment' ? sourceUrl : undefined}
            headline={cardType === 'civic_moment' ? headline : undefined}
          />
        </button>
      </div>
    </div>
  );
}
